import { useState, useEffect } from 'react';
import { useMethods } from '@lhb/hook';
import { isNotEmpty, refactorSelection } from '@lhb/func';
import { getYouthMapSelections } from 'src/common/api/youth';

/**
 * 获取青春版选址地图的距离配置选项
 * @demo
  const { distances, loading, error } = useYouthMapDistance({
    modelConfigId,
    shareData,
    options: {
      onSuccess: (data) => {
        // 回调处理
        onOptionsChange?.(data);
      },
      onError: (err) => {
        console.error('加载距离选项失败:', err);
        // 可以在这里显示错误提示
      }
    }
  });
 */
export const useYouthMapDistance = ({
  modelConfigId,
  shareData,
  options = {}
}: {
  modelConfigId: number | string;
  shareData?: {
    tenantId: string;
    userId: string;
  } | null;
  options?: {
    enabled?: boolean;
    autoFetch?: boolean;
    onSuccess?: (data: any[]) => void;
    onError?: (error: Error) => void;
  };
}): {
  distances: any[];
  loading: boolean;
  error: Error | null;
  fetchSelections: () => Promise<void>;
  reset: () => void;
} => {

  const {
    enabled = true,
    autoFetch = true,
    onSuccess,
    onError
  } = options;

  const [distances, setDistances] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 初始加载
  useEffect(() => {
    if (autoFetch) {
      methods.fetchSelections();
    }
  }, [autoFetch]);

  const methods = useMethods({

    reset() {
      setDistances([]);
      setError(null);
      setLoading(false);
    },

    async fetchSelections() {
      if (!enabled) {
        return;
      }
      if (!isNotEmpty(modelConfigId)) {
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params: any = { modelConfigId };

        if (shareData) {
          params.tenantId = shareData.tenantId;
          params.userId = shareData.userId;
        }

        // console.log('params:', params);

        const res = await getYouthMapSelections(params);

        // 处理距离数据
        const distanceData = Array.isArray(res?.youthMapDistance) ? res.youthMapDistance : [];
        const processedDistances = refactorSelection(distanceData, {}, true)
          .sort((a, b) => Number(a.distance) - Number(b.distance));

        setDistances(processedDistances);
        onSuccess?.(processedDistances);
      } catch (err) {
        const error = err instanceof Error ? err : new Error('获取青年地图选择项数据失败');
        console.error('error:', error);
        setError(error);
        onError?.(error);
      } finally {
        setLoading(false);
      }
    },

  });

  return {
    distances,
    loading,
    error,
    fetchSelections: methods.fetchSelections,
    reset: methods.reset,
  };
};
